import { SERVICES } from "../backoffice/backofficeConstants";

/**
 * Pure helpers for showing backoffice work inside the engagement tracker. The grid endpoint
 * returns `backoffice: { mode, by_client, extra_clients }` when the tracker asks for it; these
 * helpers fold that into the client groups and map a backoffice engagement onto tracker columns.
 */

export const BACKOFFICE_MODES = ["off", "rolled", "rows"];
export const BACKOFFICE_MODE_LABELS = { off: "Off", rolled: "Rolled up", rows: "Rows" };

export const SERVICE_COLORS = { payroll: "#2e9e5b", monthend: "#5a7ad0", ap: "#e0892b", adhoc: "#8a5cd6" };
export const SERVICE_SHORT = { payroll: "Payroll", monthend: "Month-end", ap: "AP", adhoc: "Ad hoc" };

export const STATUS_STYLES = {
	not_started: { label: "Not Started", bg: "#f1f3f8", fg: "#5b6170" },
	in_progress: { label: "In Progress", bg: "#e8eefc", fg: "#2c4fa8" },
	pending_client: { label: "Pending Client", bg: "#fff6d6", fg: "#7a5400" },
	overdue: { label: "Overdue", bg: "#fde8e6", fg: "#b4231a" },
	complete: { label: "Complete", bg: "#e3f6e6", fg: "#176b40" },
};

export function normalizeMode(mode) {
	return BACKOFFICE_MODES.indexOf(mode) >= 0 ? mode : "off";
}

export function serviceLabel(service) {
	return (SERVICES[service] && SERVICES[service].label) || service;
}

/**
 * Attaches each client's backoffice summary to its group and appends groups for clients that
 * have backoffice work but no workflow engagement in the grid. Those extra groups respect the
 * search box (by client name) so they do not linger when a search is on. Returns a new array.
 */
export function attachBackofficeGroups(groups, backoffice, mode, search) {
	const list = (groups || []).map(g => Object.assign({}, g));
	if (!backoffice || normalizeMode(mode) === "off") return list;
	const byClient = backoffice.by_client || {};
	const present = new Set();
	list.forEach(g => {
		if (g.client_id != null) present.add(String(g.client_id));
		const summary = g.client_id != null ? byClient[g.client_id] : null;
		if (summary && summary.engagementCount) g.backoffice = summary;
	});
	const q = String(search || "")
		.trim()
		.toLowerCase();
	(backoffice.extra_clients || []).forEach(c => {
		const key = String(c.client_id);
		if (present.has(key)) return;
		const summary = byClient[c.client_id];
		if (!summary || !summary.engagementCount) return;
		if (q && String(c.client_name || "").toLowerCase().indexOf(q) === -1) return;
		list.push({ key, client_id: c.client_id, client_name: c.client_name || "(No client)", client_type: c.client_type, engagements: [], backoffice: summary, backofficeOnly: true });
		present.add(key);
	});
	list.sort((a, b) => {
		if (a.key === "__none__") return 1;
		if (b.key === "__none__") return -1;
		return (a.client_name || "").localeCompare(b.client_name || "");
	});
	return list;
}

/**
 * What a backoffice engagement shows in a given tracker column. Columns that only make sense for
 * workflow engagements (year end, team, custom fields, …) come back as `null`, which the row
 * renders hatched.
 *
 * @returns {{ kind: "text"|"service"|"status"|"date"|"na", value?: any }}
 */
export function backofficeCell(descriptor, engagement) {
	if (!descriptor || descriptor.kind !== "native") return { kind: "na" };
	switch (descriptor.col.key) {
		case "name":
			return { kind: "text", value: `${serviceLabel(engagement.service)} · ${engagement.periodLabel}`, sub: `${engagement.completeCount} / ${engagement.taskCount} tasks` };
		case "client_id":
			return { kind: "text", value: engagement.clientName || "" };
		case "flow_id":
			return { kind: "service", value: engagement.service };
		case "flow_state_id":
			return { kind: "status", value: engagement.status };
		case "due_date":
			return { kind: "date", value: engagement.dueDate, late: engagement.status === "overdue" };
		case "date_in":
			return { kind: "date", value: engagement.startDate };
		default:
			return { kind: "na" };
	}
}

/**
 * The one-line summary for the rolled-up strip.
 */
export function stripSummary(summary) {
	if (!summary) return { services: [], counts: "", periods: "", nextDue: null, overdue: 0, pips: [] };
	const periods = (summary.current || []).map(e => e.periodLabel).join(" · ");
	const pips = (summary.current || []).map(e => e.status);
	return {
		services: summary.services || [],
		counts: `${summary.seriesCount} ${summary.seriesCount === 1 ? "series" : "series"} · ${summary.openCount} open`,
		periods,
		nextDue: summary.nextDue || null,
		overdue: summary.overdueCount || 0,
		pips,
	};
}

/**
 * Keys of every client group to open on "Expand all", including backoffice-only clients.
 */
export function expandableKeys(rows, backoffice, mode) {
	const keys = new Set();
	(rows || []).forEach(r => keys.add(r.client_id == null ? "__none__" : String(r.client_id)));
	if (backoffice && normalizeMode(mode) !== "off") (backoffice.extra_clients || []).forEach(c => keys.add(String(c.client_id)));
	return keys;
}
